import PropTypes from 'prop-types';
import { SlidersHorizontal, Check, ChevronDown } from 'lucide-react';
import CaptionPreview from './CaptionPreview';
import { LANGUAGE_GROUPS, findLanguageMode } from './subtitleLanguages';

/* The single Subtitle Settings card used everywhere a video is configured
   before generating subtitles (landing UploadPrepModal and the dashboard's
   upload-config modal) so the two never drift apart again. */

const WORD_OPTIONS = [2, 3, 5, 8];

/* Segmented control: one grey track, the active segment pops out as a white
   card with indigo text so the selection reads at a glance */
const segTrack = 'flex bg-stone-100 dark:bg-stone-800 rounded-xl p-1 gap-1';
const segBtn = (active) =>
  `flex-1 min-w-0 py-2 text-[11px] sm:text-xs font-bold rounded-lg transition-all font-jetbrains whitespace-nowrap ${active
    ? 'bg-white dark:bg-stone-700 text-indigo-600 dark:text-indigo-400 shadow-sm ring-1 ring-stone-200 dark:ring-stone-600'
    : 'text-stone-500 hover:text-stone-700 dark:text-stone-400 dark:hover:text-stone-200'
  }`;

const CompactLanguagePicker = ({ value, onChange }) => {
  const { group } = findLanguageMode(value);
  return (
    <div className="space-y-2">
      <div className="relative">
      <select
        aria-label="Subtitle language"
        value={group.id}
        onChange={(event) => onChange(LANGUAGE_GROUPS.find((item) => item.id === event.target.value).modes[0].value)}
        className="block w-full min-w-0 appearance-none rounded-xl border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 pl-3 pr-10 py-3 text-sm font-jakarta text-stone-900 dark:text-stone-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
      >
        {LANGUAGE_GROUPS.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      <ChevronDown aria-hidden="true" className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-500 dark:text-stone-400" />
      </div>
      {group.modes.length > 1 && (
        <div className={segTrack} role="group" aria-label="Caption script">
          {group.modes.map((mode) => (
            <button key={mode.value} type="button" aria-pressed={value === mode.value} onClick={() => onChange(mode.value)} className={segBtn(value === mode.value)}>
              {mode.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

CompactLanguagePicker.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

const SubtitleSettingsModal = ({
  file,
  info,
  maxWords,
  language,
  onMaxWordsChange,
  onLanguageChange,
  audioCleanup = false,
  onAudioCleanupChange,
  onCancel,
  onConfirm,
  confirmLabel = 'Generate Subtitles',
  saving = false,
  footnote,
}) => (
  <div className="fixed inset-0 z-[200] flex items-center justify-center" onClick={onCancel}>
    <div className="absolute inset-0 bg-stone-900/50 backdrop-blur-sm" />
    {/* Keep the header and actions visible; only the settings can scroll. */}
    <div
      className="relative flex flex-col bg-white dark:bg-stone-900 rounded-3xl shadow-2xl p-4 sm:p-6 w-full max-w-md sm:max-w-2xl mx-4 max-h-[calc(100dvh-2rem)] overflow-hidden animate-in zoom-in-95 fade-in duration-200"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex shrink-0 items-center gap-3 mb-5">
        <div className="p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
          <SlidersHorizontal className="w-5 h-5" />
        </div>
        <div>
          <h3 className="text-lg font-bold font-display text-stone-900 dark:text-stone-100">Subtitle Settings</h3>
          <p className="text-xs text-stone-500 dark:text-stone-400 font-jakarta">Choose how your captions will look.</p>
        </div>
      </div>

      {/* grid-cols-1 matters: Tailwind's minmax(0,1fr) track can shrink, while
          the default implicit `auto` column sizes to the filename chip's
          unbreakable nowrap min-content and blows out the card on mobile */}
      <div className="grid min-h-0 grid-cols-1 sm:grid-cols-2 gap-x-6 overflow-y-auto overscroll-contain">
        <div className="min-w-0">
          {info?.name && (
            <div className="flex items-center gap-2 bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 rounded-xl px-4 py-2.5 mb-5">
              <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
              <span title={info.name} className="text-xs text-emerald-700 dark:text-emerald-400 font-jakarta font-medium truncate">
                {info.name}
                {info.sizeStr ? ` · ${info.sizeStr}` : ''}
                {info.durationStr ? ` · ${info.durationStr}` : ''}
              </span>
            </div>
          )}

          {/* Live preview: how the chosen size/language will look on the video */}
          <CaptionPreview file={file} maxWords={maxWords} language={language} />
        </div>

        <div className="min-w-0">
          {/* Words Per Line */}
          <div className="space-y-2.5 mb-5">
            <div className="flex justify-between items-center">
              <label className="text-sm font-bold text-stone-900 dark:text-stone-100 font-display">Words Per Line</label>
              <span className="text-sm text-indigo-600 dark:text-indigo-400 font-mono font-bold bg-indigo-50 dark:bg-indigo-950/40 px-2 py-0.5 rounded-lg">
                {maxWords === 'auto' ? 'AUTO' : maxWords}
              </span>
            </div>
            <div className={segTrack}>
              <button onClick={() => onMaxWordsChange('auto')} className={segBtn(maxWords === 'auto')}>Auto</button>
              {WORD_OPTIONS.map((n) => (
                <button key={n} onClick={() => onMaxWordsChange(n)} className={segBtn(maxWords === n)}>{n}</button>
              ))}
            </div>
            <p className="text-xs text-stone-400 dark:text-stone-500 font-jakarta leading-relaxed">
              2–3 for reels · 5–8 for longer videos
            </p>
          </div>

          {/* Subtitle Language */}
          <div className="space-y-2.5 mb-5">
            <label className="text-sm font-bold text-stone-900 dark:text-stone-100 font-display">Subtitle Language</label>
            <CompactLanguagePicker value={language} onChange={onLanguageChange} />
            <p className="text-xs text-stone-400 dark:text-stone-500 font-jakarta leading-relaxed">
              {language === 'english' ? 'Translates your speech into English captions.' : 'Choose the language and script for your captions.'}
            </p>
          </div>

          {/* Background music: vocal separation before transcription */}
          {onAudioCleanupChange && (
            <label className="flex items-start gap-3 rounded-xl border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-800/40 p-3 mb-5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!audioCleanup}
                onChange={(e) => onAudioCleanupChange(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stone-300 dark:border-stone-600 text-indigo-600 focus:ring-indigo-500"
                aria-label="Video has background music"
              />
              <span className="min-w-0">
                <span className="block text-sm font-bold text-stone-900 dark:text-stone-100 font-display">Video has background music</span>
                <span className="block text-xs text-stone-400 dark:text-stone-500 font-jakarta leading-relaxed">
                  Reduces music to hear speech clearly. Takes a little longer.
                </span>
              </span>
            </label>
          )}
        </div>
      </div>{/* end two-column grid */}

      {/* Actions */}
      <div className="shrink-0 border-t border-stone-200 dark:border-stone-800 pt-4">
      <div className="flex gap-3">
        <button
          onClick={onCancel}
          className="flex-1 px-4 py-3 rounded-xl text-sm font-bold font-jetbrains text-stone-600 dark:text-stone-300 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={saving}
          className="flex-[2] px-4 py-3 rounded-xl text-sm font-bold font-jetbrains bg-stone-900 text-white hover:bg-stone-800 dark:bg-stone-100 dark:text-stone-900 dark:hover:bg-white transition-all shadow-lg hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {saving ? 'Getting ready...' : confirmLabel}
        </button>
      </div>
      {footnote && (
        <p className="text-xs text-center font-jakarta text-stone-400 dark:text-stone-500 mt-3">
          {footnote}
        </p>
      )}
      </div>
    </div>
  </div>
);

SubtitleSettingsModal.propTypes = {
  file: PropTypes.object,
  info: PropTypes.shape({
    name: PropTypes.string,
    sizeStr: PropTypes.string,
    durationStr: PropTypes.string,
  }),
  maxWords: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  language: PropTypes.string.isRequired,
  onMaxWordsChange: PropTypes.func.isRequired,
  onLanguageChange: PropTypes.func.isRequired,
  audioCleanup: PropTypes.bool,
  onAudioCleanupChange: PropTypes.func,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  confirmLabel: PropTypes.string,
  saving: PropTypes.bool,
  footnote: PropTypes.string,
};

export default SubtitleSettingsModal;
